import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/Button';
import { EmptyState } from '@/components/ui/EmptyState';
import { ErrorState } from '@/components/ui/ErrorState';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { ROUTES } from '@/lib/constants/routes';
import { useContactPerk, useLivePerks, useRedeemPerk } from '../hooks/useTrustedPartners';
import { PERKS_PREVIEW_COUNT, TRUSTED_PARTNERS_STRINGS as S } from '../trusted-partners.constants';
import { PerkCard } from './PerkCard';

export interface PerksSectionProps {
  /**
   * Show only the newest offers with a "View all offers" link to the Member
   * Perks page (the Trusted Partners page). Off on the Perks page itself,
   * which lists every offer.
   */
  preview?: boolean;
}

/**
 * Startup Perks & Special Offers section (FR-30, MYN-107): every live,
 * non-expired offer, newest first. In preview mode it shows the newest six and
 * always links to the Startup Perks page (MYN-252), replacing the earlier
 * expand-in-place.
 */
export function PerksSection({ preview = false }: PerksSectionProps) {
  const query = useLivePerks();
  const redeem = useRedeemPerk();
  const contact = useContactPerk();
  const perks = query.data ?? [];
  const visible = preview ? perks.slice(0, PERKS_PREVIEW_COUNT) : perks;

  return (
    <section aria-labelledby="perks-heading">
      <h2 id="perks-heading" className="text-p4 font-semibold text-navy">
        {S.PERKS_HEADING}
      </h2>
      <p className="mb-4 text-p6 text-slate">{S.PERKS_SUBTITLE}</p>
      {query.isPending && (
        <div className="flex justify-center py-12">
          <LoadingSpinner />
        </div>
      )}
      {query.isError && <ErrorState description={S.PERKS_LOAD_FAILED} onRetry={() => void query.refetch()} />}
      {query.isSuccess && perks.length === 0 && (
        <EmptyState title={S.PERKS_EMPTY_TITLE} description={S.PERKS_EMPTY_DESCRIPTION} />
      )}
      {query.isSuccess && perks.length > 0 && (
        <>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {visible.map((perk) => (
              <PerkCard
                key={perk.id}
                perk={perk}
                onClaim={(offerId) => redeem.mutate(offerId)}
                onContact={(offerId) => contact.mutate(offerId)}
              />
            ))}
          </div>
          {/* Always shown in preview, even when every offer fits: it is the
              way into the Startup Perks page from here (UX sheet v2). */}
          {preview && (
            <div className="mt-6 flex justify-center">
              <Button asChild variant="outline" className="px-6! py-2! text-sm">
                <Link to={ROUTES.TRUSTED_PARTNERS_PERKS}>{S.VIEW_ALL_OFFERS}</Link>
              </Button>
            </div>
          )}
        </>
      )}
    </section>
  );
}
